import React from 'react';
import type { MouseEvent } from 'react';
import type { Theme } from '@material-ui/core';
import {
    Popper,
    Box,
    IconButton,
    ClickAwayListener,
    MenuList,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import { Edit, Delete } from '@material-ui/icons';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useTranslation } from 'react-i18next';

const styles = (theme: Theme) => ({
    popper: {
        background: theme.palette.background.paper,
        border: '1px solid transparent',
        borderColor: theme.palette.primary.dark,
        borderRadius: theme.shape.borderRadius,
        zIndex: 1000,
    },
    button: {
        borderRadius: '50%',
        cursor: 'pointer',
    },
    menuItem: {
        padding: theme.spacing(1, 2),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
});

interface MappingMenuPopperProps {
    reportId: string;
    open: boolean;
    anchorEl: HTMLElement | null;
    onToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => void;
    menuButtonRef: React.RefObject<HTMLButtonElement>;
}

export const MappingMenuPopper: React.FC<MappingMenuPopperProps> = ({
    reportId,
    open,
    anchorEl,
    onToggle,
    onClose,
    onEdit,
    onDelete,
    menuButtonRef,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);

    const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onToggle(event);
    };

    const handleEdit = (event: MouseEvent) => {
        event.stopPropagation();
        onClose();
        onEdit();
    };

    const handleDelete = (event: MouseEvent) => {
        event.stopPropagation();
        onClose();
        onDelete();
    };

    return (
        <ClickAwayListener onClickAway={onClose}>
            <div>
                <IconButton
                    color="inherit"
                    aria-label="Open menu"
                    onClick={handleToggle}
                    className={classes.button}
                    ref={menuButtonRef}
                >
                    <Illustration icon={IconName.OPTIONS} />
                </IconButton>

                <Popper
                    id={open ? `mapping-menu-popper-${reportId}` : undefined}
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    transition={true}
                    className={classes.popper}
                >
                    <Box>
                        <MenuList>
                            <MenuItem
                                onClick={handleEdit}
                                className={classes.menuItem}
                            >
                                <ListItemIcon>
                                    <Edit fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t(
                                        'reportMapping.mappingEdit.title',
                                    )}
                                />
                            </MenuItem>
                            <MenuItem
                                onClick={handleDelete}
                                className={classes.menuItem}
                            >
                                <ListItemIcon>
                                    <Delete fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t(
                                        'reportMapping.mappingDelete.title',
                                    )}
                                />
                            </MenuItem>
                        </MenuList>
                    </Box>
                </Popper>
            </div>
        </ClickAwayListener>
    );
};
